import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@eduviaos/ui-kit";
import { AttendanceTab } from "@/components/team-pay/AttendanceTab";
import { LeaveTab } from "@/components/team-pay/LeaveTab";
import { PayTab } from "@/components/team-pay/PayTab";
import { SettingsTab } from "@/components/team-pay/SettingsTab";
import { useTeamWords } from "@/lib/teamPay/words";
import { useTeamPayAccess } from "@/hooks/useTeamPayAccess";
import { istYearMonth, monthLabel, shiftMonth } from "@/lib/teamPay/api";
import { PayrollPage } from "@/pages/payroll/PayrollPage";

/**
 * Phase SC-22d — Team pay: one place for the people a centre pays.
 * Pay (what is owed and paid), Attendance (register + approvals), Leave,
 * Pay runs (the former Payroll page) and Settings. Accounting keeps a
 * team-cost summary that links here.
 *
 * The route sits behind `<RouteModuleGate module="payroll">`; the BE
 * re-checks module + permission on every call.
 */

const TABS = ["pay", "attendance", "leave", "runs", "settings"] as const;
type TabId = (typeof TABS)[number];

function isTab(v: string | null): v is TabId {
  return v !== null && (TABS as readonly string[]).includes(v);
}

function parseMonth(v: string | null): [number, number] | null {
  const m = v ? /^(\d{4})-(\d{2})$/.exec(v) : null;
  if (!m) return null;
  const month = Number(m[2]);
  return month >= 1 && month <= 12 ? [Number(m[1]), month] : null;
}

export function TeamPayPage() {
  const words = useTeamWords();
  const access = useTeamPayAccess();
  const [params, setParams] = useSearchParams();
  const [ty, tm] = istYearMonth();
  const [year, month] = parseMonth(params.get("month")) ?? [ty, tm];
  const tabParam = params.get("tab");
  const tab: TabId = isTab(tabParam) ? tabParam : access.canView ? "pay" : "attendance";
  const isCurrent = year === ty && month === tm;

  const update = (next: { tab?: TabId; month?: [number, number] }) => {
    const p = new URLSearchParams(params);
    if (next.tab) p.set("tab", next.tab);
    if (next.month) {
      const [y, m] = next.month;
      if (y === ty && m === tm) p.delete("month");
      else p.set("month", `${y}-${String(m).padStart(2, "0")}`);
    }
    setParams(p, { replace: true });
  };

  const header = (
    <PageHeader>
      <PageHeaderTop>
        <PageHeaderTitle>
          <PageHeaderHeading>{`${words.team} pay`}</PageHeaderHeading>
          <PageHeaderDescription>
            Attendance, pay, advances and leave for the people you pay.
          </PageHeaderDescription>
        </PageHeaderTitle>
        {tab !== "settings" && tab !== "runs" && tab !== "leave" ? (
          <PageHeaderActions>
            <div className="border-border/70 flex items-center rounded-lg border">
              <Button
                variant="ghost"
                size="icon"
                className="size-9 rounded-r-none"
                aria-label="Previous month"
                onClick={() => update({ month: shiftMonth(year, month, -1) })}
              >
                <ChevronLeft aria-hidden="true" className="size-4" />
              </Button>
              <span
                className="min-w-32 px-2 text-center text-sm font-medium tabular-nums"
                aria-live="polite"
              >
                {monthLabel(year, month)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 rounded-l-none"
                aria-label="Next month"
                disabled={isCurrent}
                onClick={() => update({ month: shiftMonth(year, month, 1) })}
              >
                <ChevronRight aria-hidden="true" className="size-4" />
              </Button>
            </div>
          </PageHeaderActions>
        ) : null}
      </PageHeaderTop>
    </PageHeader>
  );

  if (access.missingAll) {
    return (
      <div className="space-y-6">
        {header}
        <Card className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">Restricted area</CardTitle>
            <CardDescription>
              {words.team} pay is visible to administrators only. Ask an admin to give you the{" "}
              <span className="font-medium">View team pay</span> permission if you need it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      <Tabs value={tab} onValueChange={(v) => isTab(v) && update({ tab: v })}>
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList className="w-max">
            {access.canView ? <TabsTrigger value="pay">Pay</TabsTrigger> : null}
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="leave">Leave</TabsTrigger>
            {access.canViewPayRuns ? <TabsTrigger value="runs">Pay runs</TabsTrigger> : null}
            {access.canView ? <TabsTrigger value="settings">Settings</TabsTrigger> : null}
          </TabsList>
        </div>
        {access.canView ? (
          <TabsContent value="pay" className="mt-5">
            <PayTab
              year={year}
              month={month}
              canManage={access.canManage}
              canClose={access.canManage && access.canManagePayRuns}
            />
          </TabsContent>
        ) : null}
        <TabsContent value="attendance" className="mt-5">
          <AttendanceTab
            year={year}
            month={month}
            canMark={access.canMarkAttendance}
            canApprove={access.canApprove}
          />
        </TabsContent>
        <TabsContent value="leave" className="mt-5">
          <LeaveTab canApprove={access.canApprove} />
        </TabsContent>
        {access.canViewPayRuns ? (
          <TabsContent value="runs" className="mt-5">
            <PayrollPage embedded />
          </TabsContent>
        ) : null}
        {access.canView ? (
          <TabsContent value="settings" className="mt-5">
            <SettingsTab canManage={access.canManage} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
